import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { X, Sunrise, Sunset, Play, Square, Clock, MapPin, Loader2 } from "lucide-react";

const EVENT_META = {
  shift_start: { icon: Sunrise, cls: "bg-blue-100 text-blue-700", key: "evShiftStart" },
  shift_end: { icon: Sunset, cls: "bg-slate-200 text-slate-600", key: "evShiftEnd" },
  job_in: { icon: Play, cls: "bg-emerald-100 text-emerald-700", key: "evJobIn" },
  job_out: { icon: Square, cls: "bg-red-100 text-red-700", key: "evJobOut" },
};

export const TechDayModal = ({ tech, onClose }) => {
  const { t, lang } = useLang();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const mapRef = useRef(null);
  const mapObj = useRef(null);

  useEffect(() => {
    setData(null);
    api.get(`/workforce/timeline/${tech.tech_id}?date=${date}`).then(setData).catch(() => setData({ events: [], route: [] }));
  }, [tech.tech_id, date]);

  useEffect(() => {
    if (!data || !mapRef.current) return;
    if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; }
    const route = data.route || [];
    if (route.length === 0) return;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(map);
    const pts = route.map((p) => [p.lat, p.lng]);
    L.polyline(pts, { color: "#2563eb", weight: 3, opacity: 0.8, dashArray: "6 6" }).addTo(map);
    route.forEach((p, i) => {
      const marker = L.marker([p.lat, p.lng], {
        icon: L.divIcon({
          className: "",
          html: `<div style="background:${p.type === "job_in" ? "#059669" : p.type === "job_out" ? "#dc2626" : "#2563eb"};color:#fff;border-radius:9999px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">${i + 1}</div>`,
          iconSize: [22, 22], iconAnchor: [11, 11],
        }),
      }).addTo(map);
      marker.bindPopup(`<b>${i + 1}. ${t(EVENT_META[p.type]?.key || p.type)}</b><br/>${p.label || ""}<br/>${new Date(p.time).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`);
    });
    map.fitBounds(L.latLngBounds(pts).pad(0.25));
    mapObj.current = map;
    return () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; } };
  }, [data]); // eslint-disable-line

  const events = data?.events || [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="tech-day-modal" className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold">{(tech.tech_name || "?").slice(0, 1).toUpperCase()}</span>
            <div>
              <h2 className="text-base font-semibold text-slate-900">{tech.tech_name}</h2>
              <span className="text-xs text-slate-400">{t("dayTimeline")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="timeline-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" />
            <button data-testid="timeline-close" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
          </div>
        </div>

        {!data ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : (
          <div className="p-5 space-y-4">
            {(data.route || []).length > 0 ? (
              <div data-testid="timeline-map" ref={mapRef} className="h-64 rounded-xl border border-slate-200 overflow-hidden z-0" />
            ) : (
              <div className="h-24 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400 gap-1.5">
                <MapPin className="w-4 h-4" />{t("noGpsPoints")}
              </div>
            )}

            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("dayTimeline")}</div>
              {events.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">{t("noActivityDay")}</div>}
              <div className="space-y-0.5">
                {events.map((ev, i) => {
                  const meta = EVENT_META[ev.type] || EVENT_META.job_in;
                  const Icon = meta.icon;
                  return (
                    <div key={i} data-testid={`timeline-event-${i}`} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${meta.cls}`}><Icon className="w-3.5 h-3.5" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800">{t(meta.key)}{ev.label ? ` · ${ev.label}` : ""}</div>
                        {ev.unit_name && <div className="text-[11px] text-slate-400">{ev.unit_name}</div>}
                      </div>
                      {ev.hours != null && <span className="text-[11px] font-bold text-slate-500 tabular-nums">{Number(ev.hours).toFixed(2)}h</span>}
                      <span className="text-xs text-slate-500 tabular-nums inline-flex items-center gap-1"><Clock className="w-3 h-3" />{ev.time ? new Date(ev.time).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
